import * as React from "react";
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    Popover,
    TextField,
    Typography,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import {SxProps, Theme} from "@mui/material/styles";
import {DayPicker, DateRange} from "react-day-picker";
import "react-day-picker/style.css";
import {format, isBefore, startOfDay} from "date-fns";

export type DateRangeValue = [Date | null, Date | null];

export interface DateRangePickerActionContext {
    value: DateRangeValue;
    onCancel: () => void;
    onAccept: () => void;
    onClear: () => void;
    onToday: () => void;
}

export interface DateRangePickerProps {
    value: DateRangeValue;
    onChange?: (value: DateRangeValue) => void;
    onAccept?: (value: DateRangeValue) => void;

    disablePast?: boolean;
    shouldDisableDate?: (date: Date) => boolean;

    renderDayContent?: (date: Date, ctx: { disabled: boolean }) => React.ReactNode;
    renderActions?: (ctx: DateRangePickerActionContext) => React.ReactNode;

    textFieldSx?: SxProps<Theme>;
    textFieldClassName?: string;
    textFieldPlaceholder?: string;
    layoutClassName?: string;
}

const DATE_FORMAT = "MM/dd/yyyy";

function formatRange(value: DateRangeValue): string {
    const [from, to] = value || [null, null];
    if (!from && !to) return "";
    if (from && !to) return format(from, DATE_FORMAT);
    if (from && to) return `${format(from, DATE_FORMAT)} – ${format(to, DATE_FORMAT)}`;
    return "";
}

function tupleToRange(value: DateRangeValue): DateRange | undefined {
    const [from, to] = value || [null, null];
    if (!from && !to) return undefined;
    return {from: from || undefined, to: to || undefined};
}

function rangeToTuple(range: DateRange | undefined): DateRangeValue {
    if (!range) return [null, null];
    return [range.from || null, range.to || null];
}

function formatHeaderRange(value: DateRangeValue): string {
    const [from, to] = value || [null, null];
    const fmt = (d: Date) => format(d, "MMM d");
    if (from && to) return `${fmt(from)} – ${fmt(to)}`;
    if (from) return `${fmt(from)} – End`;
    return "Start – End";
}

export const DateRangePicker = (props: DateRangePickerProps) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));

    const [open, setOpen] = React.useState(false);
    const [draft, setDraft] = React.useState<DateRangeValue>(props.value);
    const [viewedMonth, setViewedMonth] = React.useState<Date>(props.value?.[0] || new Date());
    const anchorRef = React.useRef<HTMLDivElement>(null);

    // Sync draft from props only when the picker opens. External value changes
    // while the picker is open are intentionally ignored — the user is mid-pick.
    React.useEffect(() => {
        if (open) {
            setDraft(props.value);
            setViewedMonth(props.value?.[0] || new Date());
        }
    }, [open]);

    const handleOpen = () => setOpen(true);

    const handleSelect = (range: DateRange | undefined) => {
        const next = rangeToTuple(range);
        setDraft(next);
        props.onChange?.(next);
    };

    const handleAccept = () => {
        props.onAccept?.(draft);
        setOpen(false);
    };

    const handleCancel = () => {
        setDraft(props.value);
        props.onChange?.(props.value);
        setOpen(false);
    };

    const handleClear = () => {
        const cleared: DateRangeValue = [null, null];
        setDraft(cleared);
        props.onChange?.(cleared);
    };

    // Scroll the visible month to today without mutating the selection — matches
    // the legacy MUI `onSetToday` behavior. Avoids a footgun where clicking
    // "today" would silently clear an in-progress range.
    const handleToday = () => {
        setViewedMonth(startOfDay(new Date()));
    };

    const dpDisabled = (date: Date) => {
        if (props.disablePast && isBefore(date, startOfDay(new Date()))) return true;
        if (props.shouldDisableDate?.(date)) return true;
        return false;
    };

    const dayPickerComponents = props.renderDayContent
        ? {
              DayButton: (dayProps: any) => {
                  const {day, modifiers, children, ...buttonProps} = dayProps;
                  const date: Date = day.date;
                  const disabled = !!modifiers?.disabled;
                  return (
                      <button {...buttonProps}>
                          <span style={{position: "relative", display: "inline-flex", alignItems: "flex-start", justifyContent: "center", paddingTop: "4px", width: "100%", height: "100%"}}>
                              {children}
                              <span style={{position: "absolute", left: 0, right: 0, top: 0, bottom: 0, pointerEvents: "none"}}>
                                  {props.renderDayContent!(date, {disabled})}
                              </span>
                          </span>
                      </button>
                  );
              },
          }
        : undefined;

    const calendar = (
        <Box className={props.layoutClassName} sx={{p: 1}}>
            <DayPicker
                mode="range"
                selected={tupleToRange(draft)}
                onSelect={handleSelect}
                disabled={dpDisabled}
                month={viewedMonth}
                onMonthChange={setViewedMonth}
                numberOfMonths={isMobile ? 1 : 2}
                components={dayPickerComponents}
            />
            {props.renderActions ? (
                props.renderActions({
                    value: draft,
                    onCancel: handleCancel,
                    onAccept: handleAccept,
                    onClear: handleClear,
                    onToday: handleToday,
                })
            ) : (
                <DialogActions>
                    <Button onClick={handleCancel}>Cancel</Button>
                    <Button onClick={handleAccept} variant="contained">
                        Apply
                    </Button>
                </DialogActions>
            )}
        </Box>
    );

    const trigger = (
        <TextField
            ref={anchorRef}
            value={formatRange(props.value)}
            placeholder={props.textFieldPlaceholder || "Select date range"}
            onClick={handleOpen}
            InputProps={{readOnly: true}}
            sx={props.textFieldSx}
            className={props.textFieldClassName}
            fullWidth
        />
    );

    return (
        <>
            {trigger}
            {(() => {
                const header = (
                    <Box sx={{p: 3, pb: 2, borderBottom: 1, borderColor: "divider"}}>
                        <Typography sx={{fontSize: "12px", color: "text.secondary", letterSpacing: "0.08em", textTransform: "uppercase"}}>
                            Select date range
                        </Typography>
                        <Typography sx={{fontSize: "32px", fontWeight: 400, mt: 0.5}}>
                            {formatHeaderRange(draft)}
                        </Typography>
                    </Box>
                );
                return isMobile ? (
                    <Dialog open={open} onClose={handleCancel} fullScreen>
                        {header}
                        {calendar}
                    </Dialog>
                ) : (
                    <Popover
                        open={open}
                        anchorEl={anchorRef.current}
                        onClose={handleCancel}
                        anchorOrigin={{vertical: "bottom", horizontal: "left"}}
                    >
                        {header}
                        {calendar}
                    </Popover>
                );
            })()}
        </>
    );
};
